<template>
	<AppDialog :closeRight="'-10'" :closeTop="'-30'" dialogName="AddCustomDomainDialog" width="620">
		<Col class="dialog-content rounded px-9 py-4">
			<Row class="title-text mb-6">{{ wizardStep == 1 ? 'Add a custom domain' : 'Connect your domain to ply' }}</Row>

			<template v-if="wizardStep === 1">
				<h3>Enter a subdomain or a domain</h3>
				<h5 class="secondary-font-color">The domain should be owned by you and not used by any another service</h5>
				<br />
				<SignupTextField
					ref="domainField"
					class="width-100"
					autofocus
					type="text"
					single-line
					v-model="domain"
					iconName="url"
					:rules="rules"
					placeholder="portals.acme.com"
					prefix="https://"
				/>
				<Row class="flex-centerize">
					<AnemicButton :disabled="isButtonDisabled" :loading="isLoading" @click="addDomain" class="pa-0 action-button" tile>
						Add Domain
					</AnemicButton>
				</Row>
			</template>
			<template v-else>
				<h3>Set the following record on your DNS provider to continue:</h3>
				<h5 class="secondary-font-color">
					Not sure how? <a href="https://docs.ply.io/publish-share-and-debug/publish-to-a-custom-domain" target="_blank">Here's a guide</a>
				</h5>
				<br />
				<Row class="width-100">
					<Col class="flex-1">
						<h2>Type</h2>
						<h3>{{ dnsType }}</h3>
					</Col>
					<Col class="flex-1">
						<h2>Name</h2>
						<h3>{{ dnsName }}</h3>
					</Col>
					<Col class="flex-1">
						<h2>Value</h2>
						<h3>{{ dnsValue }}</h3>
					</Col>
				</Row>
				<br />
				<h5 class="secondary-font-color">
					It usually takes 10 minutes for DNS to propagate, and can take up to 24 hours.<br />Using Cloudflare or Vercel?
					<a href="https://docs.ply.io/publish-share-and-debug/publish-to-a-custom-domain#comments-about-specific-providers" target="_blank"
						>Read this</a
					>
				</h5>
				<br />
				<Row class="flex-centerize">
					<AnemicButton :loading="isLoading" @click="verifyDomain" class="pa-0 action-button" tile> Verify Configuration</AnemicButton>
				</Row>
			</template>
		</Col>
	</AppDialog>
</template>
<script>
import BillingService from '../Billing/billing-service';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { addCustomDomain, verifyCustomDomain } from '@/store/modules/customDomains/actions';
import { mapActionsShorthand } from '@/store/utils/mappers';
import SignupTextField from '@/views/userRegistration/components/SignupTextField.vue';
export default {
	name: 'AddCustomDomainDialog',
	components: { AppDialog, SignupTextField },
	props: {
		currentDomain: { type: Object, required: false },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		isButtonDisabled() {
			return this.rules.some((rule) => rule(this.domain) !== true);
		},
		dnsName() {
			const subdomain = this.extractSubdomain(this.domain);
			if (!subdomain) {
				return '@'; // Apex
			}
			return subdomain;
		},
		dnsType() {
			if (this.dnsName === '@') return 'A';
			return 'CNAME';
		},
		dnsValue() {
			if (this.dnsType === 'A') return '76.76.21.21';
			return 'domains.ply.io.';
		},
	},
	methods: {
		...mapActionsShorthand({ addCustomDomain, verifyCustomDomain }),
		extractSubdomain(domain) {
			const parts = domain.split('.');
			return parts.length > 2 ? parts.slice(0, -2).join('.') : null;
		},
		close() {
			this.dialogPromiseHandlers.resolve();
			AppDialogManagerService.closeCurrentDialog();
		},
		async addDomain() {
			this.isLoading = true;
			try {
				this.customDomain = await this.addCustomDomain({ domain: this.domain });
				this.wizardStep = 2;
			} catch (err) {
				console.error(err);
				const isBillingError = BillingService.isBillingError(err);
				if (!isBillingError) {
					this.$toast.error('Something went wrong, please try again later.\nif this issue persists, please contact support');
				}
			} finally {
				this.isLoading = false;
			}
		},
		async verifyDomain() {
			this.isLoading = true;
			try {
				const verifyResult = await this.verifyCustomDomain({ domainId: this.customDomain.id });
				if (verifyResult.verified) {
					this.$toast.success('Domain verified successfully');
					this.close();
				} else {
					this.$toast.error('Domain verification failed. it might take up to 24 hours to propagate');
				}
			} catch (err) {
				console.error(err);
				this.$toast.error('Something went wrong, please try again later');
			} finally {
				this.isLoading = false;
			}
		},
	},
	mounted() {
		if (this.currentDomain) {
			this.customDomain = this.currentDomain;
			this.domain = this.currentDomain.domain;
			this.wizardStep = 2;
		}
	},
	data() {
		return {
			domain: '',
			customDomain: null,
			wizardStep: 1,
			isLoading: false,
			rules: [
				(v) => v.length > 1 || 'Must be at least 2 characters',
				(v) => !!v.match(/^(?!https?).*\..+$/) || 'Must be a valid domain without http or https',
			],
		};
	},
};
</script>

<style scoped lang="scss">
.dialog-content {
	width: 620px;
	overflow: hidden;
	background: var(--white);
}

.rounded {
	border-radius: 10px;
}

.action-button {
	width: 132px !important;
	height: 40px !important;
	font-size: 14px !important;
	border-radius: 8px !important;
	letter-spacing: 0px !important;
	color: var(--white) !important;
	font-weight: normal !important;
	text-transform: none !important;
	background: #5858ec !important;
}
.title-text {
	font-size: 22px;
	font-weight: bold;
	color: var(--primary-font-color);
}
.subtext {
	color: var(--secondary-font-color);
	font-size: 14px;
}
</style>
